<template>
	<div ref="box" class="flickering-grid" aria-hidden="true">
		<canvas ref="canvas"></canvas>
	</div>
</template>

<script>
// Canvas background of small squares that randomly flicker. A Vue take on Magic UI's
// "Flickering Grid" (magicui.design / 21st.dev). Pauses when off-screen or hidden, and
// draws a single still frame for viewers who prefer reduced motion.
export default {
	props: {
		squareSize: { type: Number, default: 3 },
		gridGap: { type: Number, default: 7 },
		flickerChance: { type: Number, default: 0.25 },
		color: { type: String, default: "rgb(184, 151, 58)" },
		maxOpacity: { type: Number, default: 0.22 },
	},
	data() {
		return { visible: true };
	},
	mounted() {
		this.ctx = this.$refs.canvas.getContext("2d");
		this.reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
		this.resize();
		this.resizeObserver = new ResizeObserver(() => this.resize());
		this.resizeObserver.observe(this.$refs.box);
		this.intersection = new IntersectionObserver(([entry]) => {
			this.visible = entry.isIntersecting;
			if (this.visible) this.start();
		});
		this.intersection.observe(this.$refs.box);
		document.addEventListener("visibilitychange", this.onVisibility);
		this.start();
	},
	beforeUnmount() {
		cancelAnimationFrame(this.frame);
		this.resizeObserver?.disconnect();
		this.intersection?.disconnect();
		document.removeEventListener("visibilitychange", this.onVisibility);
	},
	methods: {
		resize() {
			const { width, height } = this.$refs.box.getBoundingClientRect();
			const dpr = window.devicePixelRatio || 1;
			const canvas = this.$refs.canvas;
			canvas.width = Math.round(width * dpr);
			canvas.height = Math.round(height * dpr);
			canvas.style.width = `${width}px`;
			canvas.style.height = `${height}px`;
			this.dpr = dpr;
			const step = this.squareSize + this.gridGap;
			this.cols = Math.ceil(width / step);
			this.rows = Math.ceil(height / step);
			this.squares = new Float32Array(this.cols * this.rows).map(
				() => Math.random() * this.maxOpacity
			);
			this.draw();
		},
		onVisibility() {
			if (!document.hidden) this.start();
		},
		start() {
			if (this.reduced || this.running) return;
			this.running = true;
			this.last = performance.now();
			const tick = now => {
				if (!this.visible || document.hidden) {
					this.running = false;
					return;
				}
				const dt = (now - this.last) / 1000;
				this.last = now;
				for (let i = 0; i < this.squares.length; i++) {
					if (Math.random() < this.flickerChance * dt)
						this.squares[i] = Math.random() * this.maxOpacity;
				}
				this.draw();
				this.frame = requestAnimationFrame(tick);
			};
			this.frame = requestAnimationFrame(tick);
		},
		draw() {
			const { ctx, dpr } = this;
			if (!ctx) return;
			const step = (this.squareSize + this.gridGap) * dpr;
			const size = this.squareSize * dpr;
			ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
			ctx.fillStyle = this.color;
			for (let x = 0; x < this.cols; x++) {
				for (let y = 0; y < this.rows; y++) {
					ctx.globalAlpha = this.squares[x * this.rows + y];
					ctx.fillRect(x * step, y * step, size, size);
				}
			}
			ctx.globalAlpha = 1;
		},
	},
};
</script>

<style scoped>
.flickering-grid {
	position: absolute;
	inset: 0;
	overflow: hidden;
	pointer-events: none;
}

canvas {
	display: block;
}
</style>
